import React, { useEffect, useState } from "react";
import {
  Paper,
  Typography,
  Button,
  Box,
  Grid,
  Chip,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useParams, useNavigate } from "react-router-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ReplayIcon from "@mui/icons-material/Replay";
import CancelIcon from "@mui/icons-material/Cancel";
import { SERVER_BASE_URL } from "../Config";
import { ProductInfo } from "../AllProducts";
import { formatStatus, getStatusColor, formatDeliveryMethod } from "../utils/statusUtils";

interface Provider {
  id: number;
  name: string;
  email: string;
  phone: string;
  npi: number | null;
  practiceName?: string;
}

interface Order {
  id: number;
  provider?: Provider | null;
  specialInstructions: string;
  fulfillmentAddress: React.ReactNode;
  deliveryMethod: string;
  patientAddress?: Address;
  providerAddress?: Address;
  strength: string;
  quantity: number;
  products: { product: ProductInfo }[];
  patient: PatientInformation;
  billingOption: string;
  status: string;
  needBy: string;
  isAsap: boolean;
  created: string;
  lastUpdated: string;
}

interface ProductOrder {
  quantity: number;
  strength: string;
  duration: string;
  price: number;
  provider_price: number | null;
  directions: string;
  refills: number;
  days_of_supply: number;
}

interface Address {
  id?: number;
  street: string;
  city: string;
  state: string;
  zip: string;
}

interface PatientInformation {
  id: number;
  addresses: any;
  email: string;
  name: string;
  dob: string;
  phone: string;
}


const formatDateOnly = (dateString: string) => {
  if (!dateString) return "";
  // Split the date string and create a date in local timezone to avoid off-by-one errors
  const [year, month, day] = dateString.split('T')[0].split('-');
  return new Date(parseInt(year), parseInt(month) - 1, parseInt(day)).toLocaleDateString();
};

const ProviderOrderHistoryDetail: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  let { id } = useParams();
  const [order, setOrder] = useState<Order>();
  const [productOrder, setProductOrder] = useState<ProductOrder>();
  const [product, setProduct] = useState<ProductInfo>();
  const [patientInfo, setPatientInfo] = useState<PatientInformation>();
  const navigate = useNavigate();
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [cancelError, setCancelError] = useState<string | null>(null);
  const [cancelSuccess, setCancelSuccess] = useState<string | null>(null);

  const loadOrderInfo = async () => {
    try {
      const providerToken = localStorage.getItem("providerToken");
      const response = await fetch(`${SERVER_BASE_URL}/provider_orders/${id}`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${providerToken}`,
        },
      });
      const data = await response.json();
      console.log('API Response:', data); // Debug log
      
      if (data.products && data.products.length > 0) {
        setOrder(data);
        setProductOrder(data.products[0]);
        setProduct(data.products[0].product);
        setPatientInfo(data.patient);
      } else {
        console.error('No products found in response');
      }
    } catch (error) {
      console.error('Error loading order info:', error);
    }
  };


  const handleRequestCancellation = async () => {
    setCancelLoading(true);
    setCancelError(null);

    try {
      const providerToken = localStorage.getItem("providerToken");
      const response = await fetch(`${SERVER_BASE_URL}/provider_orders/${id}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${providerToken}`,
        },
      });

      const data = await response.json();

      if (data.success) {
        setCancelSuccess("Cancellation requested successfully");
        setCancelDialogOpen(false);
        loadOrderInfo();
      } else {
        setCancelError(data.message || "Failed to request cancellation");
      }
    } catch (error) {
      setCancelError("An error occurred while requesting cancellation");
    } finally {
      setCancelLoading(false);
    }
  };

  const canRequestCancellation = () => {
    if (!order) return false;
    const nonCancellableStatuses = [
      "DELIVERED",
      "CANCELLED",
      "ARCHIVED",
      "CANCELLATION_REQUESTED"
    ];
    return !nonCancellableStatuses.includes(order.status);
  };

  const handleReorder = () => {
    if (!product || !productOrder || !order || !patientInfo) return;

    const strengthIdx = product.strengths?.findIndex(
      (s: any) =>
        (s.dosage === productOrder.strength || s.strength1 === productOrder.strength) &&
        (s.duration === productOrder.duration || s.size === productOrder.duration)
    ) ?? product.strengths?.findIndex(
      (s: any) => s.dosage === productOrder.strength || s.strength1 === productOrder.strength
    );

    navigate(`/checkout/p/${product.id}/s/${strengthIdx != null && strengthIdx >= 0 ? strengthIdx : 0}`, {
      state: {
        reorderData: {
          patient: {
            id: patientInfo.id,
            name: patientInfo.name,
            email: patientInfo.email,
            phone: patientInfo.phone,
            dob: patientInfo.dob,
          },
          quantity: productOrder.quantity,
          directions: productOrder.directions,
          refills: productOrder.refills,
          daysOfSupply: productOrder.days_of_supply,
          deliveryMethod: order.deliveryMethod,
          billingOption: order.billingOption,
          specialInstructions: (order.specialInstructions || "")
            .replace(/\n?This batch size has \d+ Rxs$/, "")
            .trim(),
          patientAddressId: order.patientAddress?.id,
          providerAddressId: order.providerAddress?.id,
        }
      }
    });
  };

  useEffect(() => {
    loadOrderInfo();
  }, []);

  if (!order || !product || !patientInfo || !productOrder) {
    return <div>Loading...</div>;
  }

  const renderInfoRow = (label: string, value: string | React.ReactNode) => (
    <Box sx={{ my: 1, textAlign: "left" }}>
      <Typography variant="body2" color="text.secondary" component="div"><strong>{label}:</strong></Typography>
      <Typography variant="body1" component="div">{value}</Typography>
    </Box>
  );


  return (
    <Box sx={{ p: 2 }}>
      {cancelError && (
        <Alert severity="error" onClose={() => setCancelError(null)} sx={{ mb: 2 }}>
          {cancelError}
        </Alert>
      )}
      {cancelSuccess && (
        <Alert severity="success" onClose={() => setCancelSuccess(null)} sx={{ mb: 2 }}>
          {cancelSuccess}
        </Alert>
      )}

      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2, gap: 1 }}>
        <Button variant="contained" color="primary" href="/history" size={isMobile ? "small" : "medium"}>
          <ArrowBackIcon sx={{ mr: 0.5 }} /> {isMobile ? "Back" : "Return to History"}
        </Button>
        {canRequestCancellation() && (
          <Button
            variant="outlined"
            color="error"
            startIcon={<CancelIcon />}
            onClick={() => setCancelDialogOpen(true)}
            size={isMobile ? "small" : "medium"}
          >
            {isMobile ? "Cancel" : "Request Cancellation"}
          </Button>
        )}
      </Box>

      <Typography variant={isMobile ? "h5" : "h4"} mb={3}>Order #{order.id}</Typography>
      
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <Paper sx={{ mb: 2, p: 2 }}>
            <Typography variant={isMobile ? "h6" : "h5"} mb={2}>Prescription Drug</Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <Box
                  sx={{
                    width: { xs: 100, sm: 150 },
                    height: { xs: 100, sm: 150 },
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: '#f5f5f5',
                    borderRadius: 1,
                    overflow: 'hidden',
                    mx: { xs: "auto", sm: 0 },
                  }}
                >
                  <img
                    src={product.imageUrl}
                    alt={product.name}
                    style={{
                      maxWidth: '100%',
                      maxHeight: '100%',
                      objectFit: 'contain',
                    }}
                  />
                </Box>
              </Grid>
              <Grid item xs={12} sm={8}>
                {renderInfoRow("Name", product.name)}
                {renderInfoRow("Type", product.type)}
                {renderInfoRow("Dosage Form", product.dosageForm)}
                {renderInfoRow("Strength", productOrder.strength)}
                {renderInfoRow("Size", productOrder.duration)}
                {renderInfoRow("Quantity", productOrder.quantity.toString())}
                {renderInfoRow("Directions", productOrder.directions)}
                {renderInfoRow("Days of Supply", productOrder.days_of_supply.toString())}
                {renderInfoRow("Refills", productOrder.refills.toString())}
              </Grid>
            </Grid>
          </Paper>

          <Paper sx={{ mb: 2, p: 2 }}>
            <Typography variant={isMobile ? "h6" : "h5"} mb={2}>Patient Information</Typography>
            {renderInfoRow("Name", patientInfo.name)}
            {renderInfoRow("Email", patientInfo.email)}
            {renderInfoRow("Phone", patientInfo.phone)}
            {renderInfoRow("Date of Birth", formatDateOnly(patientInfo.dob))}
            {renderInfoRow("Addresses", (
              <>
                {patientInfo.addresses.map((address: any, index: any) => (
                  <div key={index}>
                    {address.street}, {address.city}, {address.state} {address.zip}
                  </div>
                ))}
              </>
            ))}
          </Paper>

          <Paper sx={{ mb: 2, p: 2 }}>
            <Typography variant={isMobile ? "h6" : "h5"} mb={2}>Provider</Typography>
            {renderInfoRow("Name", order.provider?.name || "N/A")}
            {renderInfoRow("Email", order.provider?.email || "N/A")}
            {renderInfoRow("Phone", order.provider?.phone || "N/A")}
            {renderInfoRow("NPI", order.provider?.npi ? order.provider.npi.toString() : "N/A")}
            {renderInfoRow("Clinic Name", order.provider?.practiceName || "N/A")}
          </Paper>

          <Paper sx={{ mb: 2, p: 2 }}>
            <Typography variant={isMobile ? "h6" : "h5"} mb={2}>Fulfillment and Delivery</Typography>
            {renderInfoRow("Delivery Method", formatDeliveryMethod(order.deliveryMethod))}
            {renderInfoRow("Fulfillment Address", order.fulfillmentAddress)}
            {order.isAsap ? (
              renderInfoRow("Priority", <Chip label="ASAP" size="small" color="error" />)
            ) : (
              renderInfoRow("Need By Date", order.needBy ? new Date(order.needBy.includes('T') ? order.needBy : order.needBy + 'T12:00:00').toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'long',
                day: 'numeric'
              }) : "N/A")
            )}
          </Paper>

          <Paper sx={{ p: 2, mb: 2 }}>
            <Typography variant={isMobile ? "h6" : "h5"} mb={2}>Order Special Instructions</Typography>
            <Typography variant="body1" sx={{ textAlign: "left", whiteSpace: "pre-line" }}>{order.specialInstructions || "N/A"}</Typography>
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2, mb: 2 }}>
            <Typography variant={isMobile ? "h6" : "h5"} mb={2}>Order Status</Typography>
            {renderInfoRow("Current Status", (() => {
              const chipStyles: Record<string, { backgroundColor: string; color: string }> = {
                PENDING: { backgroundColor: "#ed6c02", color: "#fff" },
                DELIVERED: { backgroundColor: "#2e7d32", color: "#fff" },
                PROCESSING_EXCEPTION: { backgroundColor: "#1976d2", color: "#fff" },
                CANCELLED: { backgroundColor: "#d32f2f", color: "#fff" },
                CANCELLATION_REQUESTED: { backgroundColor: "#fff", color: "#d32f2f" },
              };
              const style = chipStyles[order.status] || {};
              return <Chip label={formatStatus(order.status)} size="medium" sx={{ ...style, fontSize: "0.95rem", mt: 0.5, border: order.status === "CANCELLATION_REQUESTED" ? "1px solid #d32f2f" : undefined }} />;
            })())}
            {renderInfoRow("Order Date", new Date(order.created).toLocaleString('en-US', {
              timeZone: 'America/New_York',
              year: 'numeric',
              month: 'short',
              day: 'numeric',
              hour: '2-digit',
              minute: '2-digit'
            }))}
            {renderInfoRow("Last Updated", new Date(order.lastUpdated).toLocaleString('en-US', {
              timeZone: 'America/New_York',
              year: 'numeric',
              month: 'short',
              day: 'numeric',
              hour: '2-digit',
              minute: '2-digit'
            }))}
          </Paper>

          <Paper sx={{ p: 2, mb: 2 }}>
            <Typography variant={isMobile ? "h6" : "h5"} mb={2}>Order Summary</Typography>
            {renderInfoRow("Order Number", `#${order.id}`)}
            {renderInfoRow("Retail Price", productOrder.price && productOrder.quantity ? `$${(productOrder.price / 100 * productOrder.quantity).toFixed(2)}` : "N/A")}
            {productOrder.provider_price != null && renderInfoRow("Provider Price", `$${(productOrder.provider_price / 100 * productOrder.quantity).toFixed(2)}`)}
            <Button
              variant="contained"
              color="primary"
              startIcon={<ReplayIcon />}
              onClick={handleReorder}
              sx={{ width: "100%", borderRadius: "20px", mt: 2 }}
            >
              Reorder
            </Button>
          </Paper>
        </Grid>
      </Grid>

      {/* Cancellation Confirmation Dialog */}
      <Dialog
        open={cancelDialogOpen}
        onClose={() => setCancelDialogOpen(false)}
        sx={{ zIndex: 1400 }}
      >
        <DialogTitle>Request Cancellation</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to request cancellation for Order #{order.id}?
            The pharmacy will be notified and will process your request.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelDialogOpen(false)} disabled={cancelLoading}>
            No, Keep Order
          </Button>
          <Button
            onClick={handleRequestCancellation}
            color="error"
            variant="contained"
            disabled={cancelLoading}
          >
            {cancelLoading ? "Requesting..." : "Yes, Request Cancellation"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ProviderOrderHistoryDetail;